import { useState } from "react"
import { getHelpCenterBase } from "../urlUtils"
import Typography from "aldoo-ra/Typography"
import Button from "aldoo-ra/Button"
import HCRender from "../hc-render"
import { showNotification } from "aldoo-ra/Notification"
import { SendHelpCenterRequest } from "../api"
import Input from "aldoo-ra/Input"
import { ChevronDownIcon, PaperClipIcon, XMarkIcon } from "@heroicons/react/24/outline"
import DropZone from "aldoo-ra/DropZone"
import TextBundle from "aldoo-ra/CMS/text-bundle"
import CMSData from "aldoo-ra/CMS/cms-data"
import Value from "aldoo-ra/Value"

const SubmitRequest = () => {
  const [currentLanguage] = Value({
    key: "current-site-language",
    defaultValue: "en",
    persistKey: "current-site-language",
  })
  const texts = TextBundle({ id: "hc_submit-request" })
  const BreadcrumbsSection = HCRender.getFragment("BreadcrumbsSection")

  const { data: categoryData } = CMSData({
    contentType: "CSRequestCategory",
    match: { "content.id": "submit-ticket", "content.type": "CSRequestCategory" },
    language: currentLanguage,
  })

  const productOptions =
    categoryData && categoryData.length > 0 ? categoryData[0]?.pairs || {} : {}

  const [email, setEmail] = useState("")
  const [product, setProduct] = useState("")
  const [description, setDescription] = useState("")
  const [files, setFiles] = useState([])
  const [errors, setErrors] = useState({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState(null)

  const breadcrumbItems = [
    {
      label: texts?.help_center || "Help Center",
      link: getHelpCenterBase(),
      onClick: () => {
        window.history.pushState(null, "", getHelpCenterBase())
        window.dispatchEvent(new CustomEvent("helpCenterNavigate"))
        window.dispatchEvent(new PopStateEvent("popstate"))
      },
    },
    { label: texts?.submit_request || "Submit a Request" },
  ]

  const handleFileDrop = (acceptedFiles) => {
    if (acceptedFiles && acceptedFiles.length > 0) {
      setFiles([...files, ...acceptedFiles].slice(0, 5))
      if (errors.file) setErrors({ ...errors, file: null })
    }
  }

  const handleRemoveFile = (index) => {
    const updated = [...files]
    updated.splice(index, 1)
    setFiles(updated)
  }

  const validateForm = () => {
    const errs = {}
    if (!email.trim()) errs.email = texts?.enter_email || "Enter email"
    if (!product) errs.product = texts?.select_product || "Please select a product."
    if (!description || !description.trim())
      errs.description = texts?.provide_description || "Please provide a description."
    const oversizedFiles = files.filter((file) => file.size > 20 * 1024 * 1024)
    if (oversizedFiles.length > 0) errs.file = texts?.file_size || "File size must be less than 20MB."
    return errs
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    e.stopPropagation()
    setSubmitError(null)

    const validationErrors = validateForm()
    if (Object.keys(validationErrors).length > 0) { setErrors(validationErrors); return }
    setErrors({})
    setIsSubmitting(true)

    try {
      const response = await SendHelpCenterRequest({
        email: email.trim(),
        tags: product ? [product] : [],
        description: description.trim(),
        files: files.length > 0 ? files : null,
      })

      if (response.error) {
        if (response.error === "no_email") setErrors({ ...errors, email: texts?.no_email_error || "Email is required." })
        else if (response.error === "no_tags") setErrors({ ...errors, product: texts?.no_product_error || "Product selection is required." })
        else if (response.error === "no_description") setErrors({ ...errors, description: texts?.no_description_error || "Description is required." })
        else if (response.error === "file_too_big") setErrors({ ...errors, file: texts?.file_size_error || "File size must be less than 20MB." })
        else if (response.error === "protection_error") {
          showNotification({
            message: texts?.message_protection_error || "Message Protection error. Please revise your message or email",
            className: "bg-error text-text_inv",
          })
        } else {
          setSubmitError(`Server error: ${response.error}`)
        }
        return
      }

      if (response.result) {
        showNotification({
          message: texts?.success_message || "Thank you! Your request will be reviewed as soon as possible.",
          className: "bg-primary text-white",
        })
        setEmail("")
        setProduct("")
        setDescription("")
        setFiles([])
      }
    } catch {
      setSubmitError(texts?.submit_error || "An error occurred while submitting your request.")
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="relative bg-gradient_hc_home min-h-screen rounded-b-[50px] md:rounded-b-[100px] pb-20 md:pb-[140px] overflow-x-hidden">
      <div className="w-full md:max-w-[705px] lg:max-w-[734px] px-4 mx-auto">
        {BreadcrumbsSection && <BreadcrumbsSection items={breadcrumbItems} />}

        <Typography variant="h1" className="text-center md:text-left mb-5 md:mb-7">
          {texts?.submit_request || "Submit a request"}
        </Typography>

        <div className="mb-5 md:mb-14">
          <Typography variant="h4_light" className="text-text mt-0 mb-5 md:mb-14">
            {texts?.subtitle || "If you have any questions, comments, or need to report an issue, don't hesitate to contact us."}
          </Typography>
          <Typography variant="body1_dim">
            {texts?.note || "Before messaging, please check our articles for answers to common questions."}
          </Typography>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-5 md:gap-[30px] max-w-[734px]">
          {/* Email */}
          <div>
            <Typography variant="body1" className="mt-0 mb-6">
              {texts?.email_label || "Your email*"}
            </Typography>
            <Input
              type="email"
              variant="hc_standard"
              value={email}
              onChange={(e) => { setEmail(e.target.value); if (errors.email) setErrors({ ...errors, email: null }) }}
              placeholder={texts?.email_placeholder || "Email address"}
              className={`w-full h-[59px] ${errors.email ? "border-error" : "border-bordered"}`}
            />
            {errors.email && <Typography variant="error">{errors.email}</Typography>}
          </div>

          {/* Product */}
          <div>
            <Typography variant="body1" className="mt-0 mb-6">
              {texts?.product_label || "Topic*"}
            </Typography>
            <div className="relative">
              <select
                value={product}
                onChange={(e) => { setProduct(e.target.value); if (errors.product) setErrors({ ...errors, product: null }) }}
                className={`appearance-none w-full h-[59px] bg-gray_100 border ${errors.product ? "border-error" : "border-bordered"} rounded-full focus:outline-none focus:ring-1 focus:ring-primary pl-7 pr-10`}
              >
                <option value="">{texts?.select_product || "Select topic"}</option>
                {Object.entries(productOptions).map(([key, label]) => (
                  <option key={key} value={key}>{texts?.[key] || label}</option>
                ))}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4">
                <ChevronDownIcon className="w-5 h-5 text-gray_700" />
              </div>
            </div>
            {errors.product && <Typography variant="error">{errors.product}</Typography>}
          </div>

          {/* Description */}
          <div>
            <Typography variant="body1" className="mt-0 mb-6">
              {texts?.description_label || "Description*"}
            </Typography>
            <textarea
              value={description}
              onChange={(e) => { setDescription(e.target.value); if (errors.description) setErrors({ ...errors, description: null }) }}
              placeholder={texts?.description_placeholder || "Include as much detail as you can."}
              className={`w-full outline-none text-text placeholder:text-gray_700 bg-gray_100 border ${errors.description ? "border-error" : "border-bordered"} rounded-[30px] focus:outline-none focus:ring-1 focus:ring-primary p-4 pl-7`}
              rows="5"
            />
            {errors.description && <Typography variant="error">{errors.description}</Typography>}
            <Typography variant="txt1_dim" className="my-0">
              {texts?.description_note || "Please include steps to reproduce, device, etc."}
            </Typography>
          </div>

          {/* Attachments */}
          <div>
            <Typography variant="body1" className="block mt-0 mb-6">
              {texts?.attachments_label || "Attachments (optional)"}
            </Typography>
            {(files.length === 0 || files.length < 5) && (
              <DropZone
                onDrop={handleFileDrop}
                maxFiles={5 - files.length}
                accept={{ "image/*": [".jpg", ".jpeg", ".png", ".gif"], "application/pdf": [".pdf"] }}
                onRender={({ isDragging, handleClick, handleDragEnter, handleDragOver, handleDragLeave, handleDrop }) => (
                  <div
                    onClick={handleClick}
                    onDragEnter={handleDragEnter}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    className={`w-full rounded-[30px] border ${isDragging ? "border-primary bg-gray_50" : "border-bordered bg-white"} p-6 flex justify-center items-center h-[140px] transition-all duration-200 cursor-pointer hover:border-primary hover:bg-gray_50`}
                  >
                    <div className="text-center">
                      <span className="text-primary">{texts?.dropzone_add_file || "Add file"}</span>
                      <span className="text-gray_700"> {texts?.dropzone_drop_files || "or drop files here"}</span>
                    </div>
                  </div>
                )}
              />
            )}
            {files.length > 0 && (
              <div className="mt-4 space-y-2">
                {files.map((file, index) => (
                  <div key={index} className="bg-primary_lt rounded-full py-2 px-4 flex items-center justify-between">
                    <div className="flex items-center">
                      <PaperClipIcon className="h-5 w-5 text-gray-700 mr-2" />
                      <Typography variant="body1_dim" className="my-0">{file.name}</Typography>
                    </div>
                    <button type="button" onClick={() => handleRemoveFile(index)} className="text-gray_500 hover:text-gray_700">
                      <XMarkIcon className="h-7 w-7 stroke-2" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            {errors.file && <Typography variant="error">{errors.file}</Typography>}
          </div>

          {/* Submit */}
          <div>
            <Button type="submit" variant="primary" className="min-w-[214px] rounded-full" disabled={isSubmitting}>
              {isSubmitting ? texts?.request_sending || "Sending..." : texts?.request_send || "Send"}
            </Button>
            {Object.keys(errors).length > 0 && (
              <Typography variant="error">
                {texts?.correct_fields || "Please correct the highlighted fields above."}
              </Typography>
            )}
            {submitError && <Typography variant="error">{submitError}</Typography>}
          </div>
        </form>
      </div>
    </div>
  )
}

export default SubmitRequest
